import { IGroupedApi } from '../api/apiCommon';
import { IDateTimeService } from '../services/dateTimeService';
import { ILoggingService } from '../services/loggingService';
import { ISportsBettingSessionCommonApi } from '../api/apiSession';
import { IApiDate2StringConverter, ISportsBettingBaseApi } from '../api/apiBase';
import { ILanguage } from 'easit--localization';
import { TicketStorage } from './mtsTicket/ticketStorage';
import { ITelemetryService } from '../services/telemetryService';
import { ISoundService } from '../services/soundService';
import { IScoreChangeMonitor } from '../services/scoreChangeMonitor';
import { SRNotificationHandler } from '../api/apiSignalRNotifications';
import { IPlayer } from './player';
import { SessionPlayer } from '../dtos/apiInitSettings';
import { ILiveStreamPlayerMonitor } from './liveStream';
import { IJavascriptStorage } from '../services/storageService';
import { IVirtualGamesApi } from '../apiVirtualgames/apiVirtualGames';
import { TicketMaxStakeVisibility } from './mtsTicket/mtsTicket';

export * from './odd';
export * from './couponOdd';
export * from './progressStateModel';
export * from './standardEvent';
export * from './liveEvent';
export * from './chat/setup';

export interface ICommonAppEnvWithSoundService {
    soundService: ISoundService; //expected for desktop MTS and MTS Live Tv only
}

export interface ICommonAppEnvScoreChangeMonitor {
    scoreChangeMonitor: IScoreChangeMonitor;
}

export interface ITicketRateSyncService {
    getRows(): Map<string, number>; //id and rate
    ratesChanged(items: { id: string, rate: number }[]): void;
}

export interface ICommonAppEnv<TApi, TSessionApi extends ISportsBettingSessionCommonApi, TCommonSettings extends ICommonAppSettings, TSettingsFromApi extends ICommonSettingsFromApi> extends ICommonAppEnvWithSoundService {
    api: IGroupedApi<TApi>;
    settings: TCommonSettings;
    settingsFromApi: TSettingsFromApi;
    dateTimeService: IDateTimeService;
    loggingService: ILoggingService;
    sessionApi: IGroupedApi<TSessionApi>;
    loggedUserId: number;
    userToken: string;
    player: IPlayer | null;
    ticketStorage: TicketStorage; //expected for mobile MTS, desktop MTS, Live MTS
    telemetryService: ITelemetryService;
    sRNotificationHandler: SRNotificationHandler;
    ticketRateSyncService?: ITicketRateSyncService | null;  //specific for MTS Desktop live
    liveStreamMonitorFactory: () => ILiveStreamPlayerMonitor;
    couponSent: (data: ICouponSentData) => void;
    sessionStorage: IJavascriptStorage;
    localStorage: IJavascriptStorage;
    routing: ICommonAppEnvRouting;
}

export interface ICommonAppEnvRouting {
    getPublicTicketPath(id: string): string;
    getLegacyPublicTicketPath(id: string): string;
}

export interface ICommonAppEnvWithSBBaseApi extends ICommonAppEnv<ISportsBettingBaseApi, ISportsBettingSessionCommonApi, ICommonAppSettings, ICommonSettingsFromApi> {
}

export interface ICommonAppEnvWithVirtualGames {
    virtualGamesApi: IGroupedApi<IVirtualGamesApi> | null; //for Tipos null
}

export interface ICouponSentData {
    isForUpdateBalanceOnly?: boolean; // true means that just to update balance, no real send
    isLive: boolean;
}

/**
 * Returns string to date converter, usually available from open context in api.
 */
export const getSBApiStringToDateConverter = (env: ICommonAppEnv<ISportsBettingBaseApi, ISportsBettingSessionCommonApi, ICommonAppSettings, ICommonSettingsFromApi>): IApiDate2StringConverter => {
    let ctx = env.api.open();
    let converter = ctx.api.toDate;
    ctx.close();
    return converter;
}

export interface ICommonTemplateSettings {
    primaryColor: string;
    switchColor: string;
    loaderColor: string;
    loaderFlashColor: string;
    otbActiveSwitchColor: string;
}

export interface IMobileCodeAdvert {
    androidLinkUrl: string;
    appleLinkUrl: string;
}

export interface IInitLoaderSettings {
    text: string;
    height: string;
}

export interface ITopXBetsButtonsSetting {
    stakeBtnValues: number[];
    potentialWinBtnValues: number[];
}

/**
 * Settings coming from wrapping web (page).
 */
export interface ICommonAppSettings {
    tokenVersion: string;
    isDebug: boolean;
    language: ILanguage;
    apiBaseUrl: string;
    currency: string;
    pointsCurrency: string;
    drawTipName: string;
    connectionTimeoutMs: number;
    matchTrackerUA: number;  // 0 means allowed, -1 means disabled (can be used in production), specific id to show to specific user only (for test), -2 show to not logged too (expected for test).
    liveStreamUA: number; // 0 means allowed, -1 means disabled (can be used in production), specific id to show to specific user only (for test), -2 show to not logged too (expected for test).
    soundFilesBaseUrl: string | null;
    template: ICommonTemplateSettings;
    legacySBUrl: string;
    loginUrl: string;
    isChatFeatureAvailable: boolean;
    stakeButtons: any[];
    canShowAliasNumberInTicket: boolean;
    canDisableSignalRSSE?: boolean; // true means do not allow Server Sent Events protocol in SignalR (mts, cz prod until solution found)
    canShowMobileAppAdvert: boolean;
    userSecureId: string | null; // SecureId of user from web
    canShowNewFeatureScore: boolean;
    canShowExtendedTicketDetail: boolean;
    canShowProductsInTicket: boolean;
    canShowOneTapBet: boolean;
    initLoader?: IInitLoaderSettings;
    ticketMaxStakeVisibility: TicketMaxStakeVisibility;
    mobileCodeAdvert: IMobileCodeAdvert;
    canShortcutMobileLiveDisciplineNames: boolean;
    freeTicketBonusUrl: string;
    canShowLiveEventDetailGroupTabs: boolean;
    canShowTopXBets: boolean;
    topXBetsButtonsSetting: ITopXBetsButtonsSetting | null;
    recaptchaSiteKey: string | null;
    ticketDetailLiveScoreRefreshTimeMs: number;
    canShowLiveEventStatusInTicketDetail: boolean;
    canFilterVideoAndMTWithSport: boolean;
}

/**
 * Settings coming from api (...InitData).
 */
export interface ICommonSettingsFromApi {
    isBetDay: boolean;
    timeForCoupon: number; // how long wait when sending live coupon (couponPay), milliseconds
    timeForCashout: number; // how long wait when sending cashout of live coupon (cashoutSubmit), milliseconds
    gamesWithReplacedPlayerBase?: Set<string>; // used on Tipos only
    gamesWithReplacedPlayerTemplate?: Set<number>; // used on Tipos, ids can be as number
    currencyToPointsRatio: number;
    ticketStakeDecimalPlaces: number;
    canPrepareTicket: boolean;
    stakeMin: number;
    combiStakeMin: number;
    canShowChat: boolean;
}

export const sessionPlayerToPlayer = (sessionPlayer: SessionPlayer): IPlayer | null => {
    if (sessionPlayer.ID > 0) {
        return {
            Id: sessionPlayer.ID,
            SecureId: sessionPlayer.SecureID,
            Permissions_LiveStream: sessionPlayer.Permissions_LiveStream
        }
    }
    return null;
}